import React, { useState, useRef, useEffect, useCallback } from "react";
import { FiSend, FiCheck, FiX, FiRefreshCw, FiCopy, FiSearch, FiChevronDown, FiChevronUp, FiClock, FiMessageCircle, FiAlertCircle } from "react-icons/fi";
import { HiSparkles, HiUser, HiStar, HiEmojiHappy, HiEmojiSad, HiHeart } from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate, useParams } from "react-router-dom";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { logEvent, logEventDebounced } from "../utils/logger";
import { getApiBase } from "../apiBase";
import { startStudentInboxPoll, isHumanQueuedChatResponse } from "../liveStudentInboxPoll";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const STAGE_TEXT = {
  typing: "Crafting your perfect response",
  waiting3: "Gathering the best information for you",
  waiting5: "Almost ready — just polishing the details",
  waiting8: "Taking a bit extra time to ensure quality"
};

const WELCOME_MESSAGES = [
  "Hello! 👋 I'm your Droog Assist companion",
  "Ready to help you with anything today",
  "Ask me questions, get instant answers ✨"
];

const GeneratingText = ({ stage }) => {
  const [visible, setVisible] = useState("");
  const [cursor, setCursor] = useState(true);
  const text = STAGE_TEXT[stage] || STAGE_TEXT.typing;

  

  useEffect(() => {
    let i = 0;
    const typing = setInterval(() => {
      setVisible(text.slice(0, i + 1));
      i = (i + 1) % (text.length + 1);
    }, 80);
    const blink = setInterval(() => setCursor(c => !c), 530);
    return () => {
      clearInterval(typing);
      clearInterval(blink);
    };
  }, [text]);

  return (
    <motion.div
      animate={{ backgroundPosition: ["0% 50%", "100% 50%"] }}
      transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
      className="flex items-center gap-2.5 text-sm font-semibold bg-gradient-to-r from-[#B89D4F] via-[#5B3A60] to-[#B89D4F] bg-[length:200%_100%] bg-clip-text text-transparent"
    >
      <motion.div animate={{ rotate: [0, 15, -15, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
        <HiSparkles className="text-[#B89D4F] text-lg" />
      </motion.div>
      <span className="font-medium">{visible}</span>
      <motion.span animate={{ opacity: cursor ? 1 : 0 }} className="text-[#5B3A60] font-mono">▋</motion.span>
    </motion.div>
  );
};

const WelcomeScreen = ({ onStart, companyName }) => {
  const [currentMessage, setCurrentMessage] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessage(prev => (prev + 1) % WELCOME_MESSAGES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-gradient-to-br from-[#F7F3F7] via-[#EDE3F0] to-[#F7F3F7] flex items-center justify-center p-4">
      <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, type: "spring" }} className="bg-white rounded-3xl p-8 sm:p-12 shadow-2xl border border-[#D9C4D9] max-w-lg w-full text-center">
        <motion.div animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.05, 1] }} transition={{ duration: 3, repeat: Infinity }} className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#5B3A60] to-[#B89D4F] flex items-center justify-center shadow-lg">
          <HiSparkles className="text-white text-3xl" />
        </motion.div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#5B3A60] mb-2">{companyName ? `${companyName} Assist` : "Droog Assist"}</h1>
        <p className="text-[#6B7280] mb-6 text-sm">Your intelligent companion for instant answers</p>
        <div className="h-12 mb-8 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.p key={currentMessage} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="text-[#5B3A60] font-medium">{WELCOME_MESSAGES[currentMessage]}</motion.p>
          </AnimatePresence>
        </div>
        <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} onClick={() => onStart("")} className="w-full py-4 px-6 rounded-2xl font-semibold text-white shadow-lg bg-gradient-to-r from-[#5B3A60] to-[#823B55] hover:from-[#6E4C72] hover:to-[#944B66] transition-all flex items-center justify-center gap-2">
          <FiMessageCircle className="text-lg" /> Start Conversation
        </motion.button>
        <p className="text-xs text-[#A89CA5] mt-6">Powered by Droog • Secure & Private</p>
      </motion.div>
    </motion.div>
  );
};

const MessageBubble = ({ message, index, isUser, onFeedback, feedbackShown, conversationEnded, messageLimitExceeded, onCopy, isExpanded, onToggleExpand }) => {
  const [showTimestamp, setShowTimestamp] = useState(false);
  const isLong = message.text?.length > 300;
  const variants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 300, damping: 25, delay: index * 0.05 } }
  };

  
  if (message.type === "recommendations") {
  const formatDuration = (d) => {
    if (!d) return "N/A";
    if (d === 1) return "1 yr";
    return `${d} yrs`;
  };

  const formatFees = (f) => {
    if (!f) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(f);
  };

  return (
    <motion.div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 relative">
      {message.data.map((item, index) => (
        <motion.div
          key={item.id}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            delay: index * 0.15,
            duration: 0.4,
            ease: "easeOut",
          }}
          className="group bg-white rounded-lg overflow-hidden border border-[#E6DAE6] shadow-sm hover:shadow-lg transition-all duration-300 ease-out hover:-translate-y-1 hover:scale-[1.03] hover:z-10 flex flex-col"
        >
          <div className="relative overflow-hidden">
            <img
              src={item.image}
              onError={(e) => {
                e.target.src =
                  "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=600&q=80";
              }}
              alt={item.university}
              className="w-full h-28 object-cover transition-transform duration-300 ease-out group-hover:scale-105"
            />
            <div className="absolute top-1.5 left-1.5 px-1.5 py-[2px] text-[8px] font-medium rounded bg-white/90 text-[#5B3A60]">
              {item.country}
            </div>
          </div>

          <div className="p-2.5 flex flex-col gap-1.5 flex-1">
            <h3 className="text-[11px] font-semibold text-[#5B3A60] leading-snug line-clamp-3">
              {item.courseName || "Course Name"}
            </h3>

            <p className="text-[10px] text-gray-500 line-clamp-2">
              {item.university}
            </p>

            <div className="flex justify-between text-[10px] text-gray-500">
              <span className="line-clamp-1">{item.studyArea}</span>
              <span>{formatDuration(item.duration)}</span>
            </div>

            <div className="flex justify-between items-center pt-1.5 border-t border-[#F3EEF3] mt-auto">
              <span className="text-[11px] font-semibold text-[#B89D4F]">
                {formatFees(item.fees)}
              </span>

              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-[52px] text-center py-[3px] text-[9px] font-medium rounded bg-gradient-to-r from-[#5B3A60] to-[#B89D4F] text-white"
              >
                View →
              </a>
            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}

  return (
    <motion.div variants={variants} initial="hidden" animate="visible" className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4 group`}>
      <div className={`flex items-end gap-2.5 max-w-[92%] sm:max-w-[85%] ${isUser ? "flex-row-reverse" : ""}`}>
        {!isUser && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.1, type: "spring" }} className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#5B3A60] to-[#B89D4F] flex items-center justify-center shadow-lg flex-shrink-0">
            <HiSparkles className="text-white text-sm" />
          </motion.div>
        )}
        <div
          className={`relative px-4 py-3 rounded-2xl shadow-md border transition-all duration-200 ${
            isUser
              ? "bg-gradient-to-br from-[#5B3A60] to-[#823B55] text-white rounded-br-md border-[#5B3A60]"
              : "bg-white text-[#5B3A60] rounded-bl-md border-[#D9C4D9] hover:shadow-lg"
          }`}
          onMouseEnter={() => setShowTimestamp(true)}
          onMouseLeave={() => setShowTimestamp(false)}
        >
          

          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => <h1 className="font-bold text-lg mt-3 mb-2 text-inherit">{children}</h1>,
              h2: ({ children }) => <h2 className="font-semibold text-base mt-3 mb-2 text-inherit">{children}</h2>,
              p: ({ children }) => <p className="text-sm leading-relaxed text-inherit">{children}</p>,
              strong: ({ children }) => <span className="font-bold text-inherit">{children}</span>,
              ul: ({ children }) => <ul className="list-disc ml-5 space-y-1 mt-2 text-inherit">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal ml-5 space-y-1 mt-2 text-inherit">{children}</ol>,
              li: ({ children }) => <li className="text-sm leading-relaxed text-inherit">{children}</li>,
              a: ({ href, children }) => <a href={href} className="text-[#B89D4F] hover:text-[#823B55] underline transition-colors" target="_blank" rel="noopener noreferrer">{children}</a>
            }}
          >
            {isLong && !isExpanded ? `${message.text?.slice(0, 300)}...` : message.text}
          </ReactMarkdown>
          {!message.isTyping &&
 !message.isTypingPlaceholder &&
 !isUser &&
 !conversationEnded &&
 !messageLimitExceeded &&
 !message.isSystemMessage && (

<div className="flex items-center gap-1 mt-1 opacity-70 group-hover:opacity-100 transition">
    {message.feedbackShown ? (
      <span className="text-xs text-[#5B3A60] font-medium flex items-center gap-1">
        <FiCheck className="text-green-500" /> Feedback received
      </span>
    ) : !message.feedback ? (
      <>
        <button
  onClick={() => onFeedback(index, "up")}
  className="p-1 rounded-full hover:bg-[#F3EAF5] transition"
>
  <HiEmojiHappy className="text-sm" />
</button>

<button
  onClick={() => onFeedback(index, "down")}
  className="p-1 rounded-full hover:bg-[#F3EAF5] transition"
>
  <HiEmojiSad className="text-sm" />
</button>

        
      </>
    ) : null}

  </div>
)}
          {isLong && (
            <button onClick={onToggleExpand} className="mt-2 text-xs text-[#B89D4F] hover:text-[#823B55] font-medium flex items-center gap-1">
              {isExpanded ? <><FiChevronUp /> Show less</> : <><FiChevronDown /> Read more</>}
            </button>
          )}
          {message.isTyping && (
            <div className="flex items-center gap-1 mt-2">
              {[0, 1, 2].map((dot) => (
                <motion.div key={dot} animate={{ y: [0, -6, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: dot * 0.15 }} className={`w-1.5 h-1.5 rounded-full ${isUser ? "bg-purple-200" : "bg-[#B89D4F]"}`} />
              ))}
            </div>
          )}
          <AnimatePresence>
            {showTimestamp && !message.isTyping && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute -bottom-5 right-2 text-[10px] text-[#A89CA5] flex items-center gap-1">
                <FiClock size={10} /> {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {isUser && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.1, type: "spring" }} className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#6B7280] to-[#5B3A60] flex items-center justify-center shadow-lg flex-shrink-0">
            <HiUser className="text-white text-sm" />
          </motion.div>
        )}

      </div>
       {/* {!message.isTyping && !message.isTypingPlaceholder && !isUser && !conversationEnded && !messageLimitExceeded && !message.isSystemMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 mt-2 ml-12 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        > 
          {feedbackShown ? (
            <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-xs text-[#5B3A60] font-medium flex items-center gap-1">
              <FiCheck className="text-green-500" /> Feedback received
            </motion.span>
          ) : !message.feedback ? (
            <>
              <motion.button whileHover={{ scale: 1.15, y: -2 }} whileTap={{ scale: 0.95 }} onClick={() => onFeedback(index, "up")} className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-[#D9C4D9] text-[#6B7280] hover:bg-[#F7F3F7] hover:border-[#B89D4F] hover:text-[#5B3A60] transition-all shadow-sm" title="Helpful">
                <HiEmojiHappy className="text-lg" />
              </motion.button>
              <motion.button whileHover={{ scale: 1.15, y: -2 }} whileTap={{ scale: 0.95 }} onClick={() => onFeedback(index, "down")} className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-[#D9C4D9] text-[#6B7280] hover:bg-[#F7F3F7] hover:border-[#B89D4F] hover:text-[#5B3A60] transition-all shadow-sm" title="Not helpful">
                <HiEmojiSad className="text-lg" />
              </motion.button>
              <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} onClick={() => onCopy(message.text)} className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-[#D9C4D9] text-[#6B7280] hover:bg-[#F7F3F7] hover:border-[#B89D4F] transition-all shadow-sm" title="Copy">
                <FiCopy size={14} />
              </motion.button>
            </>
          ) : null}
        </motion.div>
      )} */}
    </motion.div>
  );
};

const UNIVERSITY_IMAGES = [
  "https://images.unsplash.com/photo-1498243691581-b145c3f54a5a",
];

const getRandomImage = () => {
  const random = UNIVERSITY_IMAGES[Math.floor(Math.random() * UNIVERSITY_IMAGES.length)];
  return `${random}?auto=format&fit=crop&w=600&q=80`;
};

const ChatUI = ({ messages, setMessages }) => {
  const [userId, setUserId] = useState(() => {
    return localStorage.getItem('droog_user_fingerprint') || null;
  });

  const formatUUID = (id) => {
    if (!id) return id;
    if (id.includes("-")) return id;
    return `${id.slice(0, 8)}-${id.slice(8, 12)}-${id.slice(12, 16)}-${id.slice(16, 20)}-${id.slice(20)}`;
  };

  const getBrowserFingerprint = async () => {
    const storedFingerprint = localStorage.getItem('droog_user_fingerprint');
    if (storedFingerprint) {
      if (userId !== storedFingerprint) setUserId(storedFingerprint);
      return storedFingerprint;
    }
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const fingerprint = formatUUID(result.visitorId);
    localStorage.setItem('droog_user_fingerprint', fingerprint);
    setUserId(fingerprint);
    return fingerprint;
  };

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [conversationCount, setConversationCount] = useState(0);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [formTriggered, setFormTriggered] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [userHasDetails, setUserHasDetails] = useState(false); 
  const [phoneError, setPhoneError] = useState("");
  const [showConversationRating, setShowConversationRating] = useState(false);
  const [conversationRating, setConversationRating] = useState(0);
  const [conversationEnded, setConversationEnded] = useState(false);
  const [botIsTyping, setBotIsTyping] = useState(false);
  const [typingStage, setTypingStage] = useState("typing");
  const [messageLimitExceeded, setMessageLimitExceeded] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [kbEmptyError, setKbEmptyError] = useState(false);
  const [kbEmptyModal, setKbEmptyModal] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [lastBotMessage, setLastBotMessage] = useState("");
  const [toast, setToast] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const typingTimersRef = useRef([]);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  /** Ensures client timer expiry fires abandon once per session id */
  const clientTimerAbandonSentRef = useRef(false);
  const inboxPollStopRef = useRef(null);

  const stopInboxPoll = useCallback(() => {
    if (inboxPollStopRef.current) {
      inboxPollStopRef.current();
      inboxPollStopRef.current = null;
    }
  }, []);

  const ensureInboxPoll = useCallback(() => {
    if (!sessionId) return;
    if (inboxPollStopRef.current) {
      inboxPollStopRef.current();
      inboxPollStopRef.current = null;
    }
    inboxPollStopRef.current = startStudentInboxPoll({
      sessionId,
      onAgentMessage: (msg) => {
        clearTypingTimers();
        setBotIsTyping(false);
        setMessages((prev) => {
          if (msg.id && prev.some((m) => m.inboxId === msg.id)) return prev;
          return [
            ...prev.filter((m) => !m.isTypingPlaceholder),
            { role: "bot", text: msg.text, inboxId: msg.id },
          ];
        });
      },
      onModeChange: (mode) => {
        if (mode !== "human") stopInboxPoll();
      },
    });
  }, [sessionId, stopInboxPoll, setMessages]);

  useEffect(() => () => stopInboxPoll(), [stopInboxPoll]);
  useEffect(() => {
    stopInboxPoll();
  }, [sessionId, stopInboxPoll]);

  const navigate = useNavigate();
  const { tenantId, botId, sessionId: paramSessionId } = useParams();

  /** Server-side summarize + abandon; works with sendBeacon (no auth headers). */
  const signalSessionAbandonBeacon = useCallback(
    (sid) => {
      if (!sid) return;
      const payload = JSON.stringify({
        sessionId: sid,
        tenantId: tenantId || undefined,
        botId: botId || undefined,
      });
      try {
        navigator.sendBeacon(
          `${getApiBase()}/chat/session/abandon`,
          new Blob([payload], { type: "application/json" })
        );
      } catch (err) {
        console.error("[ChatUI] abandon sendBeacon failed", err);
      }
    },
    [tenantId, botId]
  );
  
  const [isReady, setIsReady] = useState(false);
  const [totalDuration, setTotalDuration] = useState(null);
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const progress = totalDuration
    ? timeLeft / totalDuration
    : 0;
    
    useEffect(() => {
  const loadFingerprint = async () => {
    const canonical = localStorage.getItem('droog_canonical_user_id');
    if (canonical) { setUserId(canonical); return; }
    const storedFingerprint = localStorage.getItem('droog_user_fingerprint');
    if (storedFingerprint) {
      setUserId(storedFingerprint);
      return;
    }
    
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const fingerprint = formatUUID(result.visitorId);
    setUserId(fingerprint);
    localStorage.setItem('droog_user_fingerprint', fingerprint);
  };
  loadFingerprint();
}, []);


const formatRecommendations = (recs) => {
  return recs.map((r, i) => ({
    id: i,
    courseName: r.course_name,
    university: r.University,
    country: r.Country,
    studyArea: r.study_area,
    duration: r.course_duration,
    fees: r.avg_yearly_tuition_fees,
    url: r.course_url,
    image: getRandomImage(),
  }));
};



  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  

  useEffect(() => {
    if (sessionId) {
      const saved = localStorage.getItem(`draft_${sessionId}`);
      if (saved) setQuery(saved);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      const timer = setTimeout(() => localStorage.setItem(`draft_${sessionId}`, query), 500);
      return () => clearTimeout(timer);
    }
  }, [query, sessionId]);

  useEffect(() => { if (!sessionId) return; const saved = localStorage.getItem(`user_message_count_${sessionId}`); if (saved !== null) setUserMessageCount(Number(saved)); }, [sessionId]);
  useEffect(() => { if (!sessionId) return; localStorage.setItem(`user_message_count_${sessionId}`, userMessageCount); }, [userMessageCount, sessionId]);
  useEffect(() => { if (!sessionId) return; const saved = localStorage.getItem(`conversation_count_${sessionId}`); if (saved !== null) setConversationCount(Number(saved)); }, [sessionId]);
  useEffect(() => { if (!sessionId) return; localStorage.setItem(`conversation_count_${sessionId}`, conversationCount); }, [conversationCount, sessionId]);

  const isConversationEnd = (text) => ["bye", "thank you", "thanks", "thankyou", "exit", "finished", "done", "that's all"].some(word => text.toLowerCase().includes(word));
  const getClosingMessage = () => "✨ Thank you for chatting! I hope I helped make your day better. Come back anytime!";
  const clearTypingTimers = () => { typingTimersRef.current.forEach(clearTimeout); typingTimersRef.current = []; };
  
  const resetChatState = useCallback(() => {
    setMessages([]);
    setConversationCount(0);
    setUserMessageCount(0);
    setShowForm(false);
    setFormTriggered(false);
    setFormData({ name: "", email: "", phone: "" });
    setPhoneError("");
    setExpandedMessages({});
    setShowSuggestions(false);
    setLastBotMessage("");
    setQuery("");
    setBotIsTyping(false);
    setTypingStage("typing");
    clearTypingTimers();
    if (sessionId) {
      localStorage.removeItem(`draft_${sessionId}`);
      localStorage.removeItem(`user_message_count_${sessionId}`);
      localStorage.removeItem(`conversation_count_${sessionId}`);
      localStorage.removeItem(`lead_form_completed_${sessionId}`);
      localStorage.removeItem(`lead_form_shown_${sessionId}`);
    }
  }, [sessionId]);

  const handleSessionExpired = useCallback(() => {
    console.log("Session expired — clearing everything");

    localStorage.removeItem("session_id");
    localStorage.removeItem("session_expires_at");

    const sid = sessionId;
    if (sid) {
      localStorage.removeItem(`draft_${sid}`);
      localStorage.removeItem(`user_message_count_${sid}`);
      localStorage.removeItem(`conversation_count_${sid}`);
      localStorage.removeItem(`lead_form_completed_${sid}`);
      localStorage.removeItem(`lead_form_shown_${sid}`);
    }

    resetChatState();

    setSessionId(null);
    setSessionExpiry(null);
    setTimeLeft(null);
    setConversationEnded(false);
    setShowConversationRating(false);
    setConversationRating(0);
    setMessageLimitExceeded(false);
    setShowWelcome(true);

    navigate(`/test-assist/${tenantId}/${botId}/chat`);
  }, [sessionId, tenantId, botId, resetChatState, navigate]);

  const handleEndSession = async () => {
  if (!sessionId) return;

  try {
    await fetch(`${getApiBase()}/sessions/terminate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    });
  } catch (err) {
    console.error("Session terminate failed", err);
  }

  // Clear everything (same pattern you already use)
  localStorage.removeItem("session_id");
  localStorage.removeItem("session_expires_at");

  if (sessionId) {
    localStorage.removeItem(`draft_${sessionId}`);
    localStorage.removeItem(`user_message_count_${sessionId}`);
    localStorage.removeItem(`conversation_count_${sessionId}`);
    localStorage.removeItem(`lead_form_completed_${sessionId}`);
    localStorage.removeItem(`lead_form_shown_${sessionId}`);
  }

  resetChatState();

  setSessionId(null);
  setSessionExpiry(null);
  setTimeLeft(null);
  setConversationEnded(false);
  setMessageLimitExceeded(false);
  setShowConversationRating(false);
  setConversationRating(0);
  setShowWelcome(true);

  navigate(`/test-assist/${tenantId}/${botId}/chat`);
};
  
  const sendTrackingData = async (sid, leadData = {}) => {
  let fingerprint = formatUUID(userId);

  if (!fingerprint) {
    fingerprint = await getBrowserFingerprint();
    setUserId(fingerprint);
  }
  console.log("Sending endUserId:", fingerprint);

  const res = await fetch(`${getApiBase()}/chat/session/start`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    endUserId: formatUUID(fingerprint),
    sessionId: sid,
    tenantId,
    botId,
    fullName: leadData.name || null,
    email: leadData.email || null,
    phone: leadData.phone || null,
    primaryChannel: "web"
  })
});

const data = await res.json();
console.log("SESSION START RESPONSE:", data);
};

  useEffect(() => {
  setIsReady(false);

  logEvent('chat_ui_mounted', { tenantId, botId, paramSessionId });

  if (paramSessionId) {
    setSessionId(paramSessionId);
    localStorage.setItem("session_id", paramSessionId);
    setShowWelcome(false);
  } else {
    const storedSession = localStorage.getItem("session_id");
    const storedExpiry = localStorage.getItem("session_expires_at");

    if (storedSession && storedExpiry && Number(storedExpiry) > Date.now() / 1000) {
      setSessionId(storedSession);
      setSessionExpiry(Number(storedExpiry));
      setShowWelcome(false);
    } else {
      resetChatState();
      localStorage.removeItem("session_id");
      localStorage.removeItem("session_expires_at");
      setSessionId(null);
      setSessionExpiry(null);
      setShowWelcome(true);
    }
  }

  setTimeout(() => setIsReady(true), 0);
}, [paramSessionId]);

  useEffect(() => {
  const fetchConfig = async () => {
    try {
      logEvent('fetching_bot_config', { tenantId, botId });

      const res = await fetch(
        `${getApiBase()}/tenant/${tenantId}/bot/${botId}/config`
      );
      
      if (!res.ok) {
        console.error("Failed to fetch config", res.status);
        return;
      }

      const data = await res.json();
      const channels = data?.configuration?.channels_enabled || {};
      const isLeadCaptureEnabled = channels?.lead_capture === true;

      setConfig({
        ...data.configuration,
        leads_enabled: isLeadCaptureEnabled,
        lead_form_trigger_after_messages:
          data.configuration?.lead_form_trigger_after_messages ?? 2
      });

      logEvent('bot_config_fetched', {
        tenantId,
        botId,
        leads_enabled: isLeadCaptureEnabled
      });

    } catch (error) {
      logEvent('bot_config_fetch_error', {
        tenantId,
        botId,
        error: error.message
      });
    }
  };

  if (tenantId && botId) {
    fetchConfig();
  }
}, [tenantId, botId]);

useEffect(() => {
  if (!sessionId) return;

  const checkSessionStatus = async () => {
    try {
      const res = await fetch(
        `${getApiBase()}/sessions/${sessionId}/status`
      );

      if (res.status === 440) {
        handleSessionExpired();
        return;
      }

      if (res.status === 404) {
        handleSessionExpired();
        return;
      }

      if (!res.ok) return;

      const data = await res.json();

      if (data.status === "reconnect_required") {
        handleSessionExpired();
        return;
      }

      if (data.timeout_info?.minutes_remaining !== undefined) {
        const remainingSeconds =
          data.timeout_info.minutes_remaining * 60;

        setTimeLeft(remainingSeconds);
        if (!totalDuration) {
          setTotalDuration(remainingSeconds);
        }

        const newExpiry =
          Date.now() / 1000 + remainingSeconds;

        setSessionExpiry(newExpiry);
        localStorage.setItem("session_expires_at", newExpiry);
      }

      if (data.live_mode === "human") {
        ensureInboxPoll();
      } else if (data.live_mode === "ai") {
        stopInboxPoll();
      }

    } catch (err) {
      console.error("Session check failed", err);
    }
  };

  checkSessionStatus();
  const interval = setInterval(checkSessionStatus, 5000);

  return () => clearInterval(interval);
}, [sessionId, handleSessionExpired, ensureInboxPoll, stopInboxPoll]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); logEventDebounced('chat_scrolled_to_bottom', { messagesCount: messages.length, showForm }); }, [messages, showForm]);

const createSession = async () => {
  let fingerprint = formatUUID(userId);
  // If fingerprint not ready yet, generate it
  if (!fingerprint) {
    fingerprint = await getBrowserFingerprint();
    setUserId(fingerprint);
  }

  try {
    setLoading(true); 
    logEvent('session_creation_initiated', { tenantId, botId });
    
    const res = await fetch(`${getApiBase()}/sessions`, { 
      method: "POST", 
      headers: { "Content-Type": "application/json" }, 
      body: JSON.stringify({ 
        end_user_id: fingerprint, 
        tenant_id: tenantId, 
        bot_id: botId 
      }) 
    });
    
    const data = await res.json();
    
    if (res.status === 400 && data.detail?.error === "no_kb_documents") {
  setKbEmptyError(true); // Trigger the empty KB UI
  setKbEmptyModal(true); // Show modal popup instead of toast
  setLoading(false);
  return; // Stop here - don't create session
}
    
    // Standard success flow
    if (data.session_id) {
      setSessionId(data.session_id); 
      localStorage.setItem("session_id", data.session_id); 
      setShowWelcome(false);
      logEvent('session_created', { sessionId: data.session_id, tenantId, botId });
      sendTrackingData(data.session_id);
      
      if (data.timeout_info?.timeout_minutes) {
        const now = Date.now() / 1000; 
        const backendExp = now + data.timeout_info.timeout_minutes * 60;
        setSessionExpiry(backendExp); 
        setTimeLeft(data.timeout_info.timeout_minutes * 60);
        setTotalDuration(data.timeout_info.timeout_minutes * 60);
        localStorage.setItem("session_expires_at", backendExp);
        logEvent('session_timeout_set', { sessionId: data.session_id, timeoutMinutes: data.timeout_info.timeout_minutes });
      }
      
      resetChatState(); 
      navigate(`/test-assist/${tenantId}/${botId}/chat/${data.session_id}`);
      logEvent('navigation_after_session_creation', { path: `/test-assist/${tenantId}/${botId}/chat/${data.session_id}` });
    } else { 
      logEvent('session_creation_failed', { response: data }); 
      setToast("⚠️ Failed to start session. Please try again.");
      setTimeout(() => setToast(null), 3000);
    }
  } catch (error) { 
    logEvent('session_creation_error', { error: error.message, tenantId, botId }); 
    setToast("⚠️ Network error. Please check your connection.");
    setTimeout(() => setToast(null), 3000);
  } finally { 
    setLoading(false); 
  }
};

  useEffect(() => {
    if (!sessionExpiry) return;
    const interval = setInterval(() => {
      const now = Date.now() / 1000; const remaining = sessionExpiry - now;
      if (remaining <= 0) { setTimeLeft(0); }
      else { setTimeLeft(remaining); }
    }, 1000);
    return () => clearInterval(interval);
  }, [sessionExpiry]);

  /** Client countdown hit zero: ensure backend runs abandon+summary even if /status polls are throttled. */
  useEffect(() => {
    if (!sessionId || timeLeft !== 0) return;
    if (clientTimerAbandonSentRef.current) return;
    clientTimerAbandonSentRef.current = true;
    logEvent("session_abandoned_client_timer", { sessionId, tenantId, botId });
    fetch(`${getApiBase()}/chat/session/abandon`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId, tenantId, botId }),
      keepalive: true,
    }).catch(() => {});
    queueMicrotask(() => handleSessionExpired());
  }, [sessionId, timeLeft, tenantId, botId, handleSessionExpired]);

  useEffect(() => {
    if (!sessionId) return;
    const completed = localStorage.getItem(`lead_form_completed_${sessionId}`);
    const wasShown = localStorage.getItem(`lead_form_shown_${sessionId}`);
    if (completed === "true") { setFormTriggered(true); setShowForm(false); }
    else if (wasShown === "true") { setFormTriggered(true); setShowForm(true); }
  }, [sessionId]);

  useEffect(() => { if (!sessionId) return; setFormData({ name: "", email: "", phone: "" }); setPhoneError(""); }, [sessionId]);

  const validatePhone = (value) => { if (!value) return false; if (!value.startsWith('+')) return false; const parsed = parsePhoneNumberFromString(value); return parsed ? parsed.isValid() : false; };
  const handlePhoneChange = (e) => { const val = e.target.value; setFormData({ ...formData, phone: val }); if (val && !validatePhone(val)) { setPhoneError("Please include country code (e.g., +1...)"); } else { setPhoneError(""); } };

  const handleFormSubmit = async () => {
    if (!validatePhone(formData.phone)) { setPhoneError("Valid phone with country code required"); return; }
    try {
      logEvent("lead_form_submission_started", { tenantId, botId, sessionId, formData });
      const parsedPhone = parsePhoneNumberFromString(formData.phone);
      const normalizedPhone = parsedPhone ? parsedPhone.format("E.164") : formData.phone;
      const createRes = await fetch(`${getApiBase()}/user/records/create`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tenant_id: tenantId, bot_id: botId, session_id: sessionId, endUserId: formatUUID(userId), name: formData.name, email: formData.email, phone: normalizedPhone }) }).then(r => r.json()).catch(() => ({}));
      const canonicalId = createRes?.canonical_end_user_id;
      if (canonicalId && canonicalId !== formatUUID(userId)) {
        localStorage.setItem('droog_canonical_user_id', canonicalId);
      }
      await fetch(`${getApiBase()}/chat/session/start`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ endUserId: formatUUID(userId), sessionId, tenantId, botId, fullName: formData.name, email: formData.email, phone: normalizedPhone, primaryChannel: "web" }) });
      logEvent("lead_form_submitted", { tenantId, botId, sessionId, formData });
      setToast("Details saved! Continuing your conversation...");
      setTimeout(() => setToast(null), 2000);
      setShowForm(false); setFormTriggered(true); localStorage.setItem(`lead_form_completed_${sessionId}`, "true");
      const lastUserMessage = messages.filter((m) => m.role === "user").pop()?.text || "";
      setTimeout(() => sendSilentMessage(lastUserMessage), 400);
    } catch (error) { logEvent("lead_form_submission_error", { error: error.message, tenantId, botId, sessionId }); }
  };

  const typeBotResponseInBubble = (fullText, onComplete) => {
  const words = fullText.split(" ");
  let currentWords = [];
  let i = 0;

  setMessages(prev => [...prev, { role: "bot", text: "", isTyping: true }]);

  const interval = setInterval(() => {
    if (i < words.length) {
      currentWords.push(words[i]);
      i++;

      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1].text = currentWords.join(" ");
        return updated;
      });
    } else {
      clearInterval(interval);

      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1].isTyping = false;
        return updated;
      });

      setBotIsTyping(false);

      // ✅ IMPORTANT
      if (onComplete) onComplete();
    }
  }, 90);
};

      const sendSilentMessage = async (text) => {
    logEvent('silent_message_sent', { text, sessionId, tenantId, botId });
    clearTypingTimers(); 
    setTypingStage("typing"); 
    setMessages(prev => [...prev, { role: "bot", isTypingPlaceholder: true }]); 
    setBotIsTyping(true);
    typingTimersRef.current.push(
      setTimeout(() => setTypingStage("waiting3"), 5000), 
      setTimeout(() => setTypingStage("waiting5"), 12000), 
      setTimeout(() => setTypingStage("waiting8"), 20000)
    );
    try {
        const safeRetrievalConfig = config?.retrieval ? {
            enabled: config.retrieval.enabled,
            mode: config.retrieval.mode,
            k_sparse: config.retrieval.k_sparse,
            k_dense: config.retrieval.k_dense,
            rrf_k: config.retrieval.rrf_k,
            top_k_rrf: config.retrieval.top_k_rrf,
            top_k_final: config.retrieval.top_k_final
        } : null;

        const res = await fetch(`${getApiBase()}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
            tenant_id: tenantId,
            bot_id: botId,
            query: text,
            end_user_id: formatUUID(userId) || await getBrowserFingerprint(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            retrieval_config: safeRetrievalConfig
          })
        });
      if (res.status === 429) { 
        const errorData = await res.json().catch(() => ({})); 
        logEvent('message_limit_reached', { sessionId, tenantId, botId, errorData }); 
        setMessageLimitExceeded(true); 
        setBotIsTyping(false); 
        setConversationEnded(true); 
        return; 
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      console.log("🔥 FULL RESPONSE:", data);
      console.log("🔥 RECOMMENDATIONS:", data.recommendations);
      if (isHumanQueuedChatResponse(data)) {
        clearTypingTimers();
        setMessages((prev) => prev.filter((m) => !m.isTypingPlaceholder));
        setBotIsTyping(false);
        ensureInboxPoll();
        return;
      }
      stopInboxPoll();
      clearTypingTimers(); 
      setMessages(prev => prev.filter(m => !m.isTypingPlaceholder));
      if (data.session_info?.minutes_remaining) { 
        const now = Date.now() / 1000; 
        const newExpiry = now + data.session_info.minutes_remaining * 60; 
        setSessionExpiry(newExpiry); 
        setTimeLeft(data.session_info.minutes_remaining * 60); 
        localStorage.setItem("session_expires_at", newExpiry); 
      }
      if (data.is_lead_captured === true) {
          setUserHasDetails(true);
          if (showForm) setShowForm(false);
      }
      const answer = data.answer || "";
      const recs = data.recommendations || [];

      const isRagUseless =
        answer.toLowerCase().includes("couldn't find") ||
        answer.toLowerCase().includes("not found") ||
        answer.toLowerCase().includes("no information");

      if (false) {
        setTimeout(() => {
          setMessages(prev => [
            ...prev,
            {
              role: "bot",
              type: "recommendations",
              data: formatRecommendations(recs)
            }
          ]);
        }, 500);
      } 
      else if (recs.length > 0) {
        const cleanAnswer = answer?.trim() || "";
        const isUselessAnswer = !cleanAnswer || cleanAnswer.length < 40;

        if (cleanAnswer && !isUselessAnswer) {
          typeBotResponseInBubble(cleanAnswer, () => {
            setMessages(prev => [
              ...prev,
              {
                role: "bot",
                type: "recommendations",
                text: "Here are some suggested courses you might be interested in:",
                data: formatRecommendations(recs)
              }
            ]);
          });
        } else {
          setMessages(prev => [
            ...prev,
            {
              role: "bot",
              type: "recommendations",
              data: formatRecommendations(recs)
            }
          ]);
        }
      } else {
        typeBotResponseInBubble(answer || "Hmm, let me try that again.");
      }
    } catch (error) { 
      clearTypingTimers(); 
      setBotIsTyping(false); 
      setMessages(prev => [...prev.filter(m => !m.isTypingPlaceholder), { role: "bot", text: "⚠️ Connection issue. Please retry." }]); 
    }
  };

    const sendMessage = async (e, overrideText = null) => {
    if (showForm) return; 
    if (e?.preventDefault) e.preventDefault();
    
    const userInput = overrideText ? overrideText : query.trim(); 
    if (!userInput) return;

    if (isConversationEnd(userInput)) {
      setMessages(prev => [...prev, { role: "user", text: userInput }]); 
      setQuery(""); 
      setUserMessageCount(c => c + 1);
      setMessages(prev => [...prev, { role: "bot", text: getClosingMessage() }]); 
      clearTypingTimers(); 
      setBotIsTyping(false);
      setConversationEnded(true); 
      setShowConversationRating(true); 
      setShowSuggestions(false); 
      return;
    }

    if (!sessionId) { 
        navigate(`/test-assist/${tenantId}/${botId}/chat`); 
        return; 
    }

    const triggerAfter = config?.lead_form_trigger_after_messages ?? 2;
    // 🔥 DEBUG: Lead Form Trigger Check
console.log("=== LEAD FORM DEBUG ===");
console.log("1. config:", config);
console.log("2. leads_enabled:", config?.leads_enabled);
console.log("3. triggerAfter:", config?.lead_form_trigger_after_messages ?? 2);
console.log("4. userMessageCount:", userMessageCount);
console.log("5. formTriggered:", formTriggered);
console.log("6. userHasDetails:", userHasDetails);
console.log("7. sessionId:", sessionId);
console.log("=======================");

    const shouldTriggerForm =
      config?.leads_enabled === true &&
      !formTriggered &&
      userMessageCount + 1 >= triggerAfter &&
      !userHasDetails;

    if (shouldTriggerForm) {
        setMessages(prev => [...prev, { role: "user", text: userInput }]); 
        setQuery(""); 
        setUserMessageCount(c => c + 1); 
        
        setShowForm(true);
        setFormTriggered(true);
        localStorage.setItem(`lead_form_shown_${sessionId}`, "true");
        logEvent("lead_form_intercepted", { sessionId, userMessageCount: userMessageCount + 1 });
        
        return; 
    }

    setMessages(prev => [...prev, { role: "user", text: userInput }]); 
    setQuery(""); 
    setUserMessageCount(c => c + 1); 
    setShowSuggestions(false);

    clearTypingTimers(); 
    setTypingStage("typing"); 
    setMessages(prev => [...prev, { role: "bot", isTypingPlaceholder: true }]); 
    setBotIsTyping(true);
    
    typingTimersRef.current.push(
        setTimeout(() => setTypingStage("waiting3"), 5000), 
        setTimeout(() => setTypingStage("waiting5"), 12000), 
        setTimeout(() => setTypingStage("waiting8"), 20000)
    );
    
    try {
        const safeRetrievalConfig = config?.retrieval ? {
            enabled: config.retrieval.enabled,
            mode: config.retrieval.mode,
            k_sparse: config.retrieval.k_sparse,
            k_dense: config.retrieval.k_dense,
            rrf_k: config.retrieval.rrf_k,
            top_k_rrf: config.retrieval.top_k_rrf,
            top_k_final: config.retrieval.top_k_final
        } : null;

        const res = await fetch(`${getApiBase()}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
            tenant_id: tenantId,
            bot_id: botId,
            query: userInput,
            end_user_id: formatUUID(userId) || await getBrowserFingerprint(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            retrieval_config: safeRetrievalConfig
          })
        });     
      
      if (res.status === 429) { 
          const errorData = await res.json().catch(() => ({})); 
          logEvent('message_limit_reached', { sessionId, tenantId, botId, errorData }); 
          setMessageLimitExceeded(true); 
          setBotIsTyping(false); 
          setConversationEnded(true); 
          return; 
      }
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      const data = await res.json();

      if (isHumanQueuedChatResponse(data)) {
        clearTypingTimers();
        setMessages((prev) => prev.filter((m) => !m.isTypingPlaceholder));
        setBotIsTyping(false);
        ensureInboxPoll();
        return;
      }
      stopInboxPoll();
      
      if (data.session_info?.minutes_remaining) { 
          const now = Date.now() / 1000; 
          const newExpiry = now + data.session_info.minutes_remaining * 60; 
          setSessionExpiry(newExpiry); 
          setTimeLeft(data.session_info.minutes_remaining * 60); 
          localStorage.setItem("session_expires_at", newExpiry); 
      }
      
      if (data.is_lead_captured === true) {
          setUserHasDetails(true);
          if (showForm) setShowForm(false);
      }
      
      clearTypingTimers(); 
      setMessages(prev => prev.filter(m => !m.isTypingPlaceholder));
      
      const answer = data.answer || "";
      const recs = data.recommendations || [];

      const isRagUseless =
        answer.toLowerCase().includes("couldn't find") ||
        answer.toLowerCase().includes("not found") ||
        answer.toLowerCase().includes("no information");

      if (false) {
        setTimeout(() => {
          setMessages(prev => [
            ...prev,
            {
              role: "bot",
              type: "recommendations",
              data: formatRecommendations(recs)
            }
          ]);
        }, 500);

      } 
      else if (recs.length > 0) {
        const cleanAnswer = answer?.trim() || "";
        const isUselessAnswer = !cleanAnswer || cleanAnswer.length < 40;

        if (cleanAnswer && !isUselessAnswer) {
          typeBotResponseInBubble(cleanAnswer, () => {
            setMessages(prev => [
              ...prev,
              {
                role: "bot",
                text: "Here are some suggested courses you might be interested in:"
              },
              {
                role: "bot",
                type: "recommendations",
                data: formatRecommendations(recs)
              }
            ]);
          });
        } else {
          setMessages(prev => [
            ...prev,
            {
              role: "bot",
              type: "recommendations",
              data: formatRecommendations(recs)
            }
          ]);
        }
      } else {
        typeBotResponseInBubble(answer || "Hmm, let me try that again.");
      }
    } catch (err) { 
        clearTypingTimers(); 
        setBotIsTyping(false); 
        setMessages(prev => [...prev.filter(m => !m.isTypingPlaceholder), { role: "bot", text: "⚠️ Something went wrong. Please try again." }]); 
    }
  };

  const handleFeedback = async (index, type) => {
    const msg = messages[index]; setMessages(prev => { const updated = [...prev]; updated[index] = { ...updated[index], feedback: type, feedbackShown: true }; return updated; });
    setTimeout(() => { setMessages(prev => { const updated = [...prev]; if (updated[index]) updated[index].feedbackShown = false; return updated; }); }, 5000);
    try { await fetch(`${getApiBase()}/chat/feedback`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tenant_id: tenantId, session_id: sessionId, bot_id: botId, message: msg.text, feedback: type }) }); }
    catch (err) { console.error("Feedback error", err); }
  };

  const handleCopy = useCallback((text) => {
    navigator.clipboard.writeText(text);
    setToast("Copied to clipboard!");
    setTimeout(() => setToast(null), 2000);
  }, []);

  const toggleExpand = (index) => {
    setExpandedMessages(prev => ({ ...prev, [index]: !prev[index] }));
  };

  // SUBMIT RATING - Clear everything and navigate
  const submitRating = async () => {
    if (!conversationRating) return;
    const sid = sessionId;
    try {
      await fetch(`${getApiBase()}/chat/conversation-rating`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tenant_id: tenantId, bot_id: botId, session_id: sid, rating: conversationRating }) });
    } catch (err) {
      console.error("Rating submit error", err);
    }
    if (sid) {
      try {
        await fetch(`${getApiBase()}/sessions/terminate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sid, tenant_id: tenantId }),
        });
      } catch (err) {
        console.error("Session terminate after rating failed", err);
      }
    }

    localStorage.removeItem("session_id");
    localStorage.removeItem("session_expires_at");
    if (sessionId) {
      localStorage.removeItem(`draft_${sessionId}`);
      localStorage.removeItem(`user_message_count_${sessionId}`);
      localStorage.removeItem(`conversation_count_${sessionId}`);
      localStorage.removeItem(`lead_form_completed_${sessionId}`);
      localStorage.removeItem(`lead_form_shown_${sessionId}`);
    }
    
    resetChatState();
    setSessionId(null);
    setSessionExpiry(null);
    setTimeLeft(null);
    setConversationEnded(false);
    setShowConversationRating(false);
    setConversationRating(0);
    setMessageLimitExceeded(false);
    setShowWelcome(true);
    
    navigate(`/test-assist/${tenantId}/${botId}/chat`);
  };

  const navigateToDashboard = useCallback(() => {
    localStorage.removeItem("session_id");
    localStorage.removeItem("session_expires_at");
    if (sessionId) {
      localStorage.removeItem(`draft_${sessionId}`);
      localStorage.removeItem(`user_message_count_${sessionId}`);
      localStorage.removeItem(`conversation_count_${sessionId}`);
      localStorage.removeItem(`lead_form_completed_${sessionId}`);
      localStorage.removeItem(`lead_form_shown_${sessionId}`);
    }
    resetChatState();
    setSessionId(null);
    setSessionExpiry(null);
    setTimeLeft(null);
    setConversationEnded(false);
    setMessageLimitExceeded(false);
    setShowConversationRating(false);
    setConversationRating(0);
    setShowWelcome(true);
    navigate(`/dashboard/${tenantId}`);
  }, [sessionId, tenantId, resetChatState, navigate]);

  const navigateToBilling = useCallback(() => {
    localStorage.removeItem("session_id");
    localStorage.removeItem("session_expires_at");
    if (sessionId) {
      localStorage.removeItem(`draft_${sessionId}`);
      localStorage.removeItem(`user_message_count_${sessionId}`);
      localStorage.removeItem(`conversation_count_${sessionId}`);
      localStorage.removeItem(`lead_form_completed_${sessionId}`);
      localStorage.removeItem(`lead_form_shown_${sessionId}`);
    }
    resetChatState();
    setSessionId(null);
    setSessionExpiry(null);
    setTimeLeft(null);
    setConversationEnded(false);
    setMessageLimitExceeded(false);
    setShowConversationRating(false);
    setConversationRating(0);
    setShowWelcome(true);
    navigate(`/billing/${tenantId}`);
  }, [sessionId, tenantId, resetChatState, navigate]);

  useEffect(() => {
    clientTimerAbandonSentRef.current = false;
  }, [sessionId]);

  useEffect(() => {
    const handleExit = () => {
      const sid = localStorage.getItem("session_id");
      if (!sid) return;
      logEvent("session_abandoned", { sessionId: sid, tenantId, botId });
      signalSessionAbandonBeacon(sid);
    };
    window.addEventListener("beforeunload", handleExit);
    return () => window.removeEventListener("beforeunload", handleExit);
  }, [tenantId, botId, signalSessionAbandonBeacon]);

  if (!isReady) {
  return null;
}

if (showWelcome && !sessionId) {
  if (kbEmptyError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F7F3F7] via-[#EDE3F0] to-[#F7F3F7] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="bg-white rounded-3xl p-8 sm:p-12 shadow-2xl border border-[#D9C4D9] max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gray-100 flex items-center justify-center">
            <FiSearch className="text-4xl text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-[#5B3A60] mb-2">No Document Available</h1>
          <p className="text-[#6B7280] mb-6">
            This assistant hasn't been taught anything yet. It needs documents to answer questions.
          </p>
          
          <div className="bg-[#F7F3F7] rounded-xl p-4 mb-6 text-left text-sm text-[#5B3A60]">
            <p className="font-semibold mb-2">Admin Action Required:</p>
            <ul className="list-disc ml-5 space-y-1 text-[#6B7280]">
              <li>Upload PDFs or Text files</li>
            </ul>
          </div>

          <motion.button 
            whileHover={{ scale: 1.02 }} 
            whileTap={{ scale: 0.98 }} 
            onClick={() => navigate(`/dashboard/${tenantId}`)}
            className="w-full py-4 px-6 rounded-2xl font-semibold text-white shadow-lg bg-gradient-to-r from-[#5B3A60] to-[#B89D4F] hover:from-[#6E4C72] hover:to-[#C4A95F] transition-all"
          >
            Go to Dashboard to Add Content
          </motion.button>
          
          <button 
            onClick={() => { setKbEmptyError(false); setMessages([]); }} 
            className="mt-4 text-xs text-[#A89CA5] hover:text-[#5B3A60] underline"
          >
            Try Again
          </button>
        </motion.div>
      </div>
    );
  }

  return <WelcomeScreen onStart={(q) => { setShowWelcome(false); createSession(); if (q) setTimeout(() => sendMessage(null, q), 800); }} companyName={localStorage.getItem('company_name')} />;
}

  const isInputDisabled = botIsTyping || conversationEnded || showForm || messageLimitExceeded || isOffline;
  const isPhoneValid = validatePhone(formData.phone);
  const filteredMessages = searchQuery ? messages.filter(m => m.text?.toLowerCase().includes(searchQuery.toLowerCase())) : messages;
  const timerPercent = timeLeft ? Math.min(100, (timeLeft / 1800) * 100) : 0;

  return (
    <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-[#F7F3F7] via-[#EDE3F0] to-[#F7F3F7] p-3 sm:p-6">
      <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="w-full sm:w-[92vw] md:w-[65vw] lg:w-[50vw] max-w-5xl h-[94vh] sm:h-[92vh] flex flex-col rounded-3xl overflow-hidden border border-[#D9C4D9] shadow-2xl bg-white">
        
        {/* Header */}
        <div className="px-4 sm:px-6 py-3.5 border-b bg-gradient-to-r from-[#5B3A60] to-[#B89D4F] flex justify-between items-center">
          <div className="flex items-center gap-3">
            <motion.div animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 2, repeat: Infinity }} className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
              <HiSparkles className="text-white text-lg" />
            </motion.div>
            <div>
              <h1 className="font-bold text-white text-sm sm:text-base">Droog Assist</h1>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <p className="text-purple-100 text-xs">Online •</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            
            {timeLeft !== null && (
  <div className="flex items-center gap-3">

    {/* Circular Countdown */}
    <div className="relative w-10 h-10">
      <svg className="w-10 h-10 rotate-[-90deg]">
        <circle
          cx="20"
          cy="20"
          r="16"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth="3"
          fill="transparent"
        />
        <motion.circle
  cx="20"
  cy="20"
  r={radius}
  stroke={
    timeLeft < 60
      ? "#ef4444"
      : timeLeft < 120
      ? "#f59e0b"
      : "#22c55e"
  }
  strokeWidth="3"
  fill="transparent"
  strokeDasharray={circumference}
  strokeDashoffset={circumference * (1 - progress)}
  strokeLinecap="round"
/>
      </svg>

      {/* Time Text */}
      <div className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-white">
        {timeLeft !== null
          ? `${Math.floor(timeLeft / 60)
              .toString()
              .padStart(2, "0")}:${Math.floor(timeLeft % 60)
              .toString()
              .padStart(2, "0")}`
          : "00:00"}
      </div>
    </div>
  </div>
)}
            <button
  onClick={handleEndSession}
  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 active:bg-white/30 transition flex items-center justify-center"
  title="End Session"
>
  <FiX className="text-white text-sm" />
</button>
          </div>
        </div>

        {/* Search Bar */}
        <AnimatePresence>
          {showSearch && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="px-4 py-2 border-b border-[#D9C4D9] bg-[#F7F3F7]">
              <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search messages..." className="w-full px-3 py-2 rounded-lg text-sm bg-white border border-[#D9C4D9] text-[#5B3A60] focus:ring-2 focus:ring-[#B89D4F] outline-none" />
            </motion.div>
          )}
        </AnimatePresence>

<div className="px-2 sm:px-6 py-[2px] bg-[#F7F3F7] border-b border-[#D9C4D9] text-[10px] text-[#5B3A60] flex items-center justify-center gap-1.5 leading-tight text-center">
  <FiAlertCircle className="text-[#B89D4F] text-[12px]" />
  <p className="leading-tight">
    Conversations tested here are recorded as real sessions and will appear in your analytics and conversation history.
  </p>
</div>
        {/* Messages Area */}
        <div className="flex-1 p-4 sm:p-5 space-y-4 overflow-y-auto bg-gradient-to-b from-transparent to-[#F7F3F7]/50">
          <AnimatePresence mode="popLayout">
            {filteredMessages.map((m, i) => {
              const isUser = m.role === "user";
              if (m.isTypingPlaceholder) return <motion.div key={`typing-${i}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex justify-start mb-4"><div className="ml-0"><GeneratingText stage={typingStage} /></div></motion.div>;
              return <MessageBubble key={i} message={m} index={i} isUser={isUser} onFeedback={handleFeedback} feedbackShown={m.feedbackShown} conversationEnded={conversationEnded} messageLimitExceeded={messageLimitExceeded} onCopy={handleCopy} isExpanded={expandedMessages[i]} onToggleExpand={() => toggleExpand(i)} />;
            })}
          </AnimatePresence>

          {/* Lead Form */}
          <AnimatePresence>
            {showForm && config?.leads_enabled && (
              <motion.div initial={{ opacity: 0, y: 30, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -20 }} transition={{ type: "spring", stiffness: 300, damping: 25 }} className="w-full p-5 sm:p-6 bg-gradient-to-br from-[#F7F3F7] to-[#EDE3F0] border border-[#D9C4D9] rounded-2xl shadow-lg mt-2">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#5B3A60] to-[#B89D4F] flex items-center justify-center flex-shrink-0 shadow-md"><HiUser className="text-white text-lg" /></div>
                  <div><p className="text-sm font-semibold text-[#5B3A60]">Almost there!</p><p className="text-xs text-[#6B7280]">Share your details to continue the conversation</p></div>
                </div>
                <div className="space-y-3.5">
                  {["name", "email"].map((field) => (
                    <motion.input key={field} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} placeholder={field.charAt(0).toUpperCase() + field.slice(1)} value={formData[field]} onChange={(e) => setFormData({ ...formData, [field]: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-[#D9C4D9] text-sm focus:ring-2 focus:ring-[#B89D4F] outline-none transition-all bg-white hover:border-[#B89D4F]" />
                  ))}
                  <div>
                    <motion.input initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} placeholder="+1 (555) 123-4567" value={formData.phone} onChange={handlePhoneChange} className={`w-full px-4 py-3 rounded-xl border text-sm focus:ring-2 outline-none transition-all bg-white hover:border-[#B89D4F] ${phoneError ? "border-red-400 focus:ring-red-400" : "border-[#D9C4D9] focus:ring-[#B89D4F]"}`} />
                    <AnimatePresence>{phoneError && (<motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-xs text-red-500 mt-1.5 flex items-center gap-1.5"><FiX className="text-red-400" /> {phoneError}</motion.p>)}</AnimatePresence>
                  </div>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleFormSubmit} disabled={!isPhoneValid} className={`w-full mt-2 py-3 rounded-xl font-semibold shadow-md transition-all text-sm flex items-center justify-center gap-2 ${!isPhoneValid ? "bg-gray-300 text-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-[#5B3A60] to-[#B89D4F] hover:from-[#6E4C72] hover:to-[#C4A95F] text-white"}`}>
                     Continue Conversation
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Rating Modal */}
          <AnimatePresence>
            {showConversationRating && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[2000] p-4" onClick={(e) => e.target === e.currentTarget && setShowConversationRating(false)}>
                <motion.div initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ type: "spring", stiffness: 400, damping: 30 }} className="bg-white border border-[#D9C4D9] w-full max-w-md rounded-3xl shadow-2xl p-7 text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#F7F3F7] to-[#EDE3F0] pointer-events-none" />
                  <div className="relative z-10">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.1, type: "spring" }} className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#F7F3F7] to-[#EDE3F0] shadow-inner"><HiStar className="text-3xl text-[#B89D4F]" /></motion.div>
                    <h2 className="text-xl font-bold text-[#5B3A60] mb-2">Rate your experience</h2>
                    <p className="text-sm text-[#6B7280] mb-6">Your feedback shapes the future of Droog Assist</p>
                    <div className="flex justify-center gap-2 mb-7">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <motion.button key={star} whileHover={{ scale: 1.3, y: -3 }} whileTap={{ scale: 0.9 }} onClick={() => setConversationRating(star)} className={`text-4xl transition-all duration-200 ${conversationRating >= star ? "text-[#B89D4F] drop-shadow-sm" : "text-gray-300 hover:text-[#B89D4F]"}`}>★</motion.button>
                      ))}
                    </div>
                    <AnimatePresence mode="wait">{conversationRating > 0 && (<motion.p key={conversationRating} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-sm font-medium text-[#5B3A60] mb-5">{conversationRating === 5 && "🌟 Excellent! We're thrilled!"}{conversationRating === 4 && "😊 Great! Thanks for the feedback"}{conversationRating === 3 && "🙂 Good! We'll keep improving"}{conversationRating === 2 && "😕 Thanks for helping us grow"}{conversationRating === 1 && "😞 We're sorry — tell us how to improve"}</motion.p>)}</AnimatePresence>
                    <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} onClick={submitRating} disabled={!conversationRating} className={`w-full py-3.5 rounded-2xl font-semibold transition-all shadow-lg ${conversationRating ? "bg-gradient-to-r from-[#5B3A60] to-[#B89D4F] hover:from-[#6E4C72] hover:to-[#C4A95F] text-white" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}>Submit Feedback</motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Limit Exceeded Modal */}
          <AnimatePresence>
            {messageLimitExceeded && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[1500] p-4">
                <motion.div initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }} transition={{ type: "spring", stiffness: 300 }} className="bg-white border border-[#D9C4D9] rounded-3xl p-7 w-full max-w-md shadow-2xl text-center relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#5B3A60] via-[#B89D4F] to-[#5B3A60]" />
                  <div className="mb-5">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#F7F3F7] to-[#EDE3F0] flex items-center justify-center mb-4"><HiSparkles className="text-2xl text-[#B89D4F]" /></div>
                    <h3 className="text-lg font-bold text-[#5B3A60] mb-2">Conversation Complete</h3>
                    <p className="text-sm text-[#6B7280] mb-1">You've reached your message limit for this session</p>
                    <p className="text-xs text-[#5B3A60] font-medium">💡 Upgrade for unlimited conversations</p>
                  </div>
                  <div className="space-y-3">
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={navigateToDashboard} className="w-full py-3 rounded-xl font-medium bg-[#F7F3F7] text-[#6B7280] hover:bg-[#EDE3F0] transition-all border border-[#D9C4D9]">Return to Dashboard</motion.button>
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={navigateToBilling} className="w-full py-3 rounded-xl font-semibold bg-gradient-to-r from-[#5B3A60] to-[#B89D4F] hover:from-[#6E4C72] hover:to-[#C4A95F] text-white shadow-md transition-all">Upgrade Plan →</motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <div ref={chatEndRef} className="h-2" />
        </div>

        {/* Input Area */}
        <div className="px-4 sm:px-6 py-4 border-t bg-white">
          <motion.div animate={{ y: showForm || conversationEnded ? 10 : 0 }} transition={{ type: "spring", stiffness: 300 }} className="flex items-center gap-3">
            <div className="flex-1 flex items-center bg-white border border-[#D9C4D9] rounded-2xl px-3 pr-2 shadow-sm">
  <input
    ref={inputRef}
    value={query}
    onChange={(e) => setQuery(e.target.value)}
    onKeyDown={(e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!isInputDisabled) sendMessage();
      }
    }}
    placeholder="Type your message..."
    disabled={isInputDisabled}
    className="flex-1 py-3 text-sm outline-none bg-transparent text-[#5B3A60]"
  />

  <motion.button
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    onClick={sendMessage}
    disabled={isInputDisabled || !query.trim()}
    className={`p-2.5 rounded-xl ${
      isInputDisabled || !query.trim()
        ? "bg-[#D9C4D9] text-[#A89CA5]"
        : "bg-gradient-to-r from-[#5B3A60] to-[#B89D4F] text-white"
    }`}
  >
    <FiSend size={18} />
  </motion.button>
</div>
          </motion.div>
          <div className="flex items-center justify-between mt-3">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="text-[10px] text-[#A89CA5] font-medium tracking-wide">
              Powered by Droog • {isOffline && <span className="text-red-400">● Offline</span>}
            </motion.p>
            <div className="flex items-center gap-3 text-[10px] text-[#6B7280]">
              <span>Press Enter to send</span>
            </div>
          </div>
        </div>

        {/* Toast Notification */}
        <AnimatePresence>
          {toast && (
            <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }} className="fixed bottom-6 right-6 px-5 py-3.5 rounded-xl bg-gradient-to-r from-[#5B3A60] to-[#B89D4F] text-white text-sm font-medium shadow-lg z-[3000] flex items-center gap-2.5">
              <FiCheck className="text-green-300" /> {toast}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      {/* 🔥 KB Empty Modal Popup */}
{kbEmptyModal && (
  <div className="fixed inset-0 z-[4000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 20 }}
      className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full mx-4 shadow-2xl border border-[#D9C4D9]"
    >
      {/* Icon */}
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#F7F3F7] flex items-center justify-center">
        <FiAlertCircle className="text-3xl text-[#B89D4F]" />
      </div>

      {/* Title */}
      <h3 className="text-xl font-bold text-[#5B3A60] text-center mb-2">
        No Document Available
      </h3>

      {/* Message */}
      <p className="text-[#6B7280] text-center mb-6">
        This bot hasn't been taught anything yet. It needs documents to answer questions.
      </p>

      {/* Action Info */}
      <div className="bg-[#F7F3F7] rounded-lg p-4 mb-6 text-sm text-[#5B3A60]">
        <p className="font-semibold mb-2">What you can do:</p>
        <ul className="list-disc ml-5 space-y-1 text-[#6B7280]">
          <li>Upload PDFs or Text files</li>
        </ul>
      </div>

      {/* Buttons */}
<div className="flex gap-3">
  {/* OK Button → Show Welcome Screen */}
  <button
    onClick={() => {
      setKbEmptyModal(false);
      setKbEmptyError(false); 
      setShowWelcome(true);  
      setMessages([]);
    }}
    className="flex-1 py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-[#5B3A60] to-[#B89D4F] hover:from-[#6E4C72] hover:to-[#C4A95F] transition-all"
  >
    OK
  </button>
  
  <button
    onClick={() => {
      setKbEmptyModal(false);
      navigate(`/knowledgeafteronboard/${tenantId}/${botId}`);
    }}
    className="flex-1 py-3 px-4 rounded-xl font-semibold text-[#5B3A60] bg-[#F7F3F7] border border-[#D9C4D9] hover:bg-[#EDE3F0] transition-all"
  >
    Continue
  </button>
</div>
    </motion.div>
  </div>
)}
    </div>
  );
};

export default ChatUI;